import { useEffect } from 'react'

/**
 * Base fallback title. Identical to the imperative default in
 * `apps/client/index.html` — kept in sync so unmounting a titled page restores
 * exactly what a cold load would show.
 */
export const DEFAULT_DOCUMENT_TITLE = 'XP Lab — Practice real data analysis'

type TitleArea = 'admin' | 'partner'

const AREA_SUFFIX: Record<TitleArea, string> = {
  admin: 'XP Lab Admin',
  partner: 'XP Lab Partner Area',
}

/**
 * Sets `document.title` to `${pageName} - ${areaSuffix}` for the lifetime of the
 * calling component, restoring {@link DEFAULT_DOCUMENT_TITLE} on unmount.
 *
 * Assigns `document.title` imperatively rather than rendering a `<title>`
 * element: this matches the shell default and sidesteps React 19's head
 * hoisting (which does not dedupe `<title>`) and the `dedupeTitleTags` logic in
 * `modules/seo`. Admin/partner pages are never prerendered, so there is no
 * public-SEO machinery to interact with.
 *
 * Dynamic titles are supported — pass the resolved entity name once loaded and
 * an id/slug placeholder before; the effect re-runs when the composed string
 * changes.
 */
export function useDocumentTitle(pageName: string, area: TitleArea): void {
  const suffix = AREA_SUFFIX[area]
  const trimmed = pageName.trim()
  const title = trimmed ? `${trimmed} - ${suffix}` : suffix

  useEffect(() => {
    document.title = title
    return () => {
      document.title = DEFAULT_DOCUMENT_TITLE
    }
  }, [title])
}
